<script setup lang="ts">
import {
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuArrow,
} from "reka-ui";
import { computed } from "vue";
import { useRoute } from "vue-router";

import { RsBrandLensSelect } from "@/components";
import StringTooltip from "@/components/Tooltip/StringTooltip.vue";

import { useRootLayout } from "@/hooks/use-root-layout";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { ModuleStatus } from "@/models/ModuleStatus";

import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useUserPermissionStore } from "@/stores/user-permission-store";

defineEmits<(e: "switchLayout") => void>();

const currentRoute = useRoute();
const routeParams = useCaseRouteParams();

const canUpload = computed(
  () => useUserPermissionStore().userPermission.canUpload && useBrandStore().featuresConfig?.quarantine !== "disabled",
);
const canAudit = computed(() => useUserPermissionStore().userPermission.canViewAudit);

const isRefreshing = computed((): boolean => {
  return useMetadataStore().moduleStatus === ModuleStatus.Refreshing;
});

const migrationDirection = computed((): string | undefined => {
  const caseStore = useCaseStore();

  if (caseStore.isMigrationSource && caseStore.isMigrationDestination) return "source and destination";
  if (caseStore.isMigrationSource) return "source";
  if (caseStore.isMigrationDestination) return "destination";
  return undefined;
});

const filteredFileCount = computed((): number => {
  return useMetadataStore().filteredFilesInCurrentFolder.length;
});

const duplicateFileCount = computed((): number => {
  return useMetadataStore()
    .files.filter((f) => f.aliases && f.aliases.length > 0)
    .map((a) => a.aliases!.length)
    .reduce((l, r) => l + r, 0);
});

const counts = computed(() => {
  return useMetadataStore().counts;
});

const routes = computed(() => {
  const { brand, casename } = routeParams.value;

  return [
    { show: canAudit.value, name: "Audit", to: `/${brand}/${casename}/audit` },
    { show: currentRoute.name !== "upload" && canUpload.value, name: "Upload", to: `/${brand}/${casename}/upload` },
    {
      show: currentRoute.name !== "quarantine" && canUpload.value,
      name: "Quarantine",
      to: `/${brand}/${casename}/quarantine`,
    },
    { show: true, name: "Jobs", to: `/${brand}/jobs` },
  ];
});

const { layout, headerRef } = useRootLayout();
</script>
<template>
  <Teleport :to="headerRef">
    <RsBrandLensSelect />
    <span v-if="!useBrandStore().viewerConfig.menuBar?.hideCounts" class="tw-text-green">
      Showing
      <strong>{{ filteredFileCount }}</strong> of
      <strong>{{ counts.totalFileCount }}</strong>
    </span>
    <StringTooltip
      v-if="counts.passwordProtectedCount > 0"
      :item="`Password protected: ${counts.passwordProtectedCount}`"
    >
      <span class="w-text-green">
        <i class="fa fa-unlock-alt"></i>
        {{ counts.passwordProtectedCount }}
      </span>
    </StringTooltip>
    <StringTooltip v-if="counts.unknownFileCount > 0" :item="`Unknown file type: ${counts.unknownFileCount}`">
      <span class="w-text-green">
        <i class="fas fa-question"></i>
        {{ counts.unknownFileCount }}
      </span>
    </StringTooltip>
    <template v-if="counts.draftFileCount > 0 || duplicateFileCount > 0">
      (
      <StringTooltip v-if="counts.draftFileCount > 0" :item="`Drafts: ${counts.draftFileCount}`">
        <span class="w-text-green">
          <i class="far fa-save"></i>
          {{ counts.draftFileCount }}
        </span>
      </StringTooltip>
      <StringTooltip v-if="duplicateFileCount" :item="`Duplicate: ${duplicateFileCount}`">
        <span class="w-text-green">
          <i class="far fa-copy"></i>
          {{ duplicateFileCount }}
        </span>
      </StringTooltip>
      )
    </template>
    <div class="tw-flex-1" aria-hidden="true"></div>
    <div
      v-if="isRefreshing"
      role="status"
      aria-live="polite"
      class="tw-text-green tw-mr-2.5 tw-space-x-1.5 tw-text-xs tw-tracking-wide"
    >
      <div class="tw-inline-flex tw-size-3 tw-items-center tw-justify-center">
        <i class="fas fa-spinner fa-spin"></i>
      </div>
      <span>Refreshing</span>
    </div>
    <div
      v-if="migrationDirection != null"
      role="status"
      aria-live="polite"
      class="tw-text-green tw-mr-2.5 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-tracking-wide"
    >
      <i class="fas fa-arrow-right-arrow-left" aria-hidden="true"></i>
      <span>Migration in progress ({{ migrationDirection }})</span>
    </div>
    <slot />
    <template v-if="layout === 'FLEX'">
      <template v-for="route in routes" :key="route.name">
        <router-link v-if="route.show" class="tw-btn-md-green invert tw-min-w-20" :to="route.to">
          {{ route.name }}
        </router-link>
      </template>
    </template>
    <DropdownMenuRoot v-else>
      <DropdownMenuTrigger class="tw-h-[1.875rem] tw-w-[2.671875rem] tw-rounded-md tw-bg-gray-300 tw-text-gray-800">
        <span class="tw-sr-only">Layout</span>
        <i aria-hidden="true" class="fas fa-ellipsis-vertical" />
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          align="end"
          :side-offset="5"
          class="tw-z-[100000] tw-flex tw-max-h-[--reka-popper-available-height] tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-gray-200 tw-p-2"
        >
          <template v-for="route in routes" :key="route.name">
            <DropdownMenuItem v-if="route.show" as-child>
              <router-link
                :to="route.to"
                class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
              >
                {{ route.name }}
              </router-link>
            </DropdownMenuItem>
          </template>
          <DropdownMenuArrow />
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenuRoot>
  </Teleport>
</template>
<style lang="scss">
.case-menubar {
  z-index: 1;
  top: 0;
  width: 100%;
  min-height: 30px;
  background-color: #2b1b4f;

  img {
    margin: 2px;
  }
}
</style>
